import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import Seo, { breadcrumbSchema } from "@/components/Seo";
import PublicLayout from "@/components/layout/PublicLayout";
import PageHeader from "@/components/PageHeader";
import JobCard from "@/components/JobCard";
import api from "@/lib/api";
import { useSite } from "@/context/SiteContext";

export default function LocationDetail() {
  const { slug } = useParams();
  const { settings } = useSite();
  const [l, setL] = useState(null);
  const [nf, setNf] = useState(false);
  useEffect(() => { setL(null); api.get(`/locations/${slug}`).then((r) => setL(r.data)).catch(() => setNf(true)); }, [slug]);
  if (nf) return <PublicLayout><div className="pt-40 pb-20 text-center"><h1 className="font-display text-2xl font-bold">Location not found</h1><Link to="/locations" className="text-brand-emerald mt-4 inline-block">← All locations</Link></div></PublicLayout>;
  if (!l) return <PublicLayout><div className="pt-40 grid place-items-center"><div className="h-10 w-10 rounded-full border-2 border-brand-emerald border-t-transparent animate-spin" /></div></PublicLayout>;

  const localBiz = {
    "@context": "https://schema.org", "@type": "LocalBusiness", name: `${settings.company_name} — ${l.name}`,
    description: l.meta_description, address: { "@type": "PostalAddress", addressLocality: l.name, addressRegion: "Karnataka", addressCountry: "IN" },
    areaServed: l.name, telephone: settings.phone,
  };

  const services = ["Manpower Recruitment", "HR Consultancy", "Staffing Solutions", "Blue-Collar Recruitment", "White-Collar Recruitment", "Hospitality Staffing"];

  return (
    <PublicLayout waMessage={`Hi Dreamzz HR & Consultancy, I need recruitment services in ${l.name}.`}>
      <Seo title={l.seo_title || `Recruitment Agency in ${l.name}`} description={l.meta_description}
        schema={[localBiz, breadcrumbSchema([{ name: "Home", url: "/" }, { name: "Locations", url: "/locations" }, { name: l.name, url: window.location.href }])] } />
      <PageHeader eyebrow={l.district} title={`Manpower & Recruitment in ${l.name}`} subtitle={l.intro} />
      <section className="max-w-4xl mx-auto px-4 sm:px-6 py-12">
        <h2 className="font-display text-2xl font-bold">Recruitment & Staffing Services in {l.name}</h2>
        <p className="text-slate-300 leading-relaxed mt-4">
          Dreamzz HR & Consultancy is a trusted manpower and recruitment partner for businesses and job seekers in {l.name}, {l.district}. We provide end-to-end staffing, HR consultancy and workforce solutions tailored to the local market.
        </p>
        <p className="text-slate-300 leading-relaxed mt-4">
          Whether you are an employer looking to hire skilled, semi-skilled or professional staff, or a job seeker searching for opportunities in {l.name}, our team connects the right talent with the right opportunity — quickly and reliably.
        </p>
        <div className="mt-8 grid sm:grid-cols-2 gap-3">
          {services.map((s) => <div key={s} className="glass rounded-xl p-4 flex items-center gap-3"><CheckCircle2 size={18} className="text-brand-emerald" /><span className="text-sm">{s} in {l.name}</span></div>)}
        </div>

        {l.jobs?.length > 0 && (
          <div className="mt-12">
            <h2 className="font-display text-2xl font-bold mb-5">Latest Jobs in {l.name}</h2>
            <div className="grid gap-4 sm:grid-cols-2">{l.jobs.map((j) => <JobCard key={j.id} job={j} />)}</div>
          </div>
        )}

        <div className="mt-12 glass-strong rounded-2xl p-8 text-center">
          <h3 className="font-display text-2xl font-bold">Hiring or Job-hunting in {l.name}?</h3>
          <div className="mt-6 flex flex-wrap gap-3 justify-center">
            <Link to="/for-employers"><Button className="bg-brand-emerald hover:bg-[#C62828] text-white font-semibold">Hire Staff in {l.name} <ArrowRight size={16} className="ml-2" /></Button></Link>
            <Link to={`/jobs?location=${encodeURIComponent(l.name)}`}><Button variant="outline" className="border-white/15">Jobs in {l.name}</Button></Link>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
